import { useState, useEffect } from "react";
import { Monitor, Wifi, WifiOff, ShieldOff, Plus, Mail, Copy, Check } from "lucide-react";
import { useUserDeviceHistory } from "../../api/queryHooks";
import { useUserDesktopDevices } from "../../api/queryHooks";
import { useRevokeDevice } from "../../api/queryHooks";
import { useInitDeviceEnrollment } from "../../api/queryHooks";
import { CompactButton } from "../Button/Buttons";
import useFeedback from "../../hooks/useFeedback";
import UserAuthStore from "../../store/userStore";
import { getServiceConfig } from "../../api/adminConfig";
import EmptyState from "../shared/EmptyState";
import ConfirmDialog from "../shared/ConfirmDialog/ConfirmDialog";
import { getTimeAgo } from "../../utils/helpers";

/**
 * DeviceManagement
 *
 * Displays all devices the user has ever paired, cross-referenced with
 * the live presence list (useUserDesktopDevices) to show online status.
 * Allows the user to revoke any active device with a confirmation dialog.
 *
 * Self-contained — reads orgId / userId from UserAuthStore internally.
 * Used in DesktopPage (/settings/desktop).
 */

const EMPTY_DEVICES = [];
const OTP_TTL_SECONDS = 600;

// Skeleton row for loading state
const DeviceRowSkeleton = () => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-fill-white dark:bg-fill-dialog-surface border border-edge-gray-200 dark:border-edge-panel-border animate-pulse">
    <div className="w-8 h-8 rounded-full bg-fill-gray-200 dark:bg-fill-dialog-border flex-shrink-0" />
    <div className="flex-1 min-w-0 flex flex-col gap-1.5">
      <div className="h-3 w-40 rounded bg-fill-gray-200 dark:bg-fill-dialog-border" />
      <div className="h-2.5 w-24 rounded bg-fill-gray-100 dark:bg-fill-dialog-surface" />
    </div>
    <div className="h-5 w-16 rounded-full bg-fill-gray-100 dark:bg-fill-dialog-surface" />
  </div>
);

const DeviceManagement = () => {
  const { showFeedback } = useFeedback();
  const userEmail = UserAuthStore((s) => s.email);
  const [revokeTarget, setRevokeTarget] = useState(null); // { device_id, client_id, device_name }
  const [showConfirm, setShowConfirm] = useState(false);  // pre-send confirmation panel
  const [enrollSentAt, setEnrollSentAt] = useState(null); // epoch ms when OTP email was sent
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [pipIndexUrl, setPipIndexUrl] = useState("");     // org package registry URL
  const [copied, setCopied] = useState(false);            // copy-to-clipboard feedback

  // Load org desktop config to get pip_index_url (non-fatal if absent or no permission)
  useEffect(() => {
    getServiceConfig("desktop")
      .then((data) => setPipIndexUrl(data?.pip_index_url || ""))
      .catch(() => {});
  }, []);

  const handleCopyInstallCmd = () => {
    const cmd = `pip install photon-desktop --index-url ${pipIndexUrl}`;
    navigator.clipboard.writeText(cmd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const { data: historyRaw, isLoading: historyLoading } = useUserDeviceHistory();
  const { data: liveRaw } = useUserDesktopDevices();
  const { mutate: revokeDevice, isPending: revoking } = useRevokeDevice();
  const { mutate: initEnrollment, isPending: enrolling } = useInitDeviceEnrollment();

  // Countdown timer — counts down from OTP_TTL_SECONDS after email is sent
  useEffect(() => {
    if (!enrollSentAt) return;
    const tick = () => {
      const r = Math.max(0, Math.round((enrollSentAt + OTP_TTL_SECONDS * 1000 - Date.now()) / 1000));
      setSecondsLeft(r);
      if (r === 0) setEnrollSentAt(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [enrollSentAt]);

  const handleSendCode = () => {
    // Pass client_id from an existing device if available, to bind the invite
    const knownClientId = historyRaw?.find((d) => d.status !== "revoked")?.client_id ?? null;
    initEnrollment(knownClientId, {
      onSuccess: () => {
        setShowConfirm(false);
        setEnrollSentAt(Date.now());
      },
      onError: (err) => {
        setShowConfirm(false);
        const msg = err?.response?.data?.error || "Failed to send enrollment email.";
        showFeedback(msg, "error");
      },
    });
  };

  const history = historyRaw || EMPTY_DEVICES;
  const live = liveRaw || EMPTY_DEVICES;

  // Build a Set of online device_ids for O(1) lookup
  const onlineIds = new Set(live.map((d) => d.device_id));

  const handleRevokeConfirm = () => {
    if (!revokeTarget) return;
    revokeDevice(
      { clientId: revokeTarget.client_id, deviceId: revokeTarget.device_id },
      { onSettled: () => setRevokeTarget(null) }
    );
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <>
      {/* Header row: label + enroll button */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ink-ink dark:text-ink-white">Your Devices</h3>
        <CompactButton
          onClick={() => setShowConfirm(true)}
          disabled={showConfirm || !!enrollSentAt}
        >
          <Plus className="w-3.5 h-3.5" />
          Connect new device
        </CompactButton>
      </div>

      {/* Pre-send confirmation panel */}
      {showConfirm && (
        <div className="mb-3 rounded-xl border border-edge-gray-200 dark:border-edge-dialog-border bg-fill-gray-50 dark:bg-fill-deep-surface overflow-hidden">
          <div className="flex items-start gap-3 p-4">
            <div className="photon-settings-icon w-8 h-8 rounded-full bg-fill-indigo-50 dark:bg-fill-primary/15 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-ink-primary dark:text-ink-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">
                Connect a new device
              </p>

              {/* Install step — shown only if admin has configured a package registry URL */}
              {pipIndexUrl ? (
                <div className="mt-2 mb-1 p-2.5 rounded-lg bg-fill-gray-100 dark:bg-fill-dialog-surface border border-edge-gray-200 dark:border-edge-dialog-border">
                  <p className="text-[11px] font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1.5">
                    Step 1 — Install on your machine
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[11px] font-mono text-ink-primary dark:text-ink-accent truncate">
                      pip install photon-desktop --index-url {pipIndexUrl}
                    </code>
                    <button
                      onClick={handleCopyInstallCmd}
                      className="flex-shrink-0 p-1 rounded text-ink-gray-400 hover:text-ink-primary dark:hover:text-ink-accent transition-colors"
                      title="Copy install command"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-ink-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500 mt-1">
                  Ensure <code className="font-mono bg-fill-gray-200 dark:bg-fill-dialog-surface px-1 rounded">photon-desktop</code> is installed on your machine before continuing.
                </p>
              )}

              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-1.5 leading-relaxed">
                {pipIndexUrl ? "Step 2 — " : ""}We'll send a 6-digit setup code to{" "}
                <span className="font-medium text-ink-gray-700 dark:text-ink-gray-300">{userEmail}</span>.
                Open a terminal on your new machine and run{" "}
                <code className="font-mono text-[11px] bg-fill-gray-200 dark:bg-fill-dialog-surface px-1 py-0.5 rounded">photon init</code>{" "}
                to enter it.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={handleSendCode}
                  disabled={enrolling}
                  className="photon-primary-action text-xs font-semibold px-3 py-1.5 rounded-lg bg-button-primary text-button-on-primary disabled:opacity-50 transition-colors"
                >
                  {enrolling ? "Sending…" : "Send code"}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={enrolling}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg text-ink-gray-500 dark:text-ink-gray-400 hover:bg-fill-gray-200 dark:hover:bg-fill-dialog-surface transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP sent banner */}
      {enrollSentAt && secondsLeft > 0 && (
        <div className="flex items-start gap-3 p-3.5 rounded-xl bg-fill-blue-50 dark:bg-fill-primary/10 border border-edge-blue-200 dark:border-edge-primary/40 mb-3">
          <Mail className="w-4 h-4 text-ink-primary dark:text-ink-accent flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ink-blue-700 dark:text-ink-blue-300">
              Check your email for a 6-digit code
            </p>
            <p className="text-xs text-ink-blue-500 dark:text-ink-blue-400 mt-0.5">
              Run <code className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-0.5 rounded">photon init</code> in your terminal and enter the code when prompted.
            </p>
          </div>
          <span className="flex-shrink-0 text-xs font-mono font-semibold text-ink-primary dark:text-ink-accent tabular-nums">
            {mm}:{ss}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {historyLoading ? (
          <>
            <DeviceRowSkeleton />
            <DeviceRowSkeleton />
          </>
        ) : history.length === 0 ? (
          <EmptyState
            icon={<Monitor className="w-7 h-7 text-ink-primary dark:text-ink-accent" />}
            title="No devices paired"
            description="Download the Photon Desktop app to connect your machine and run local tools."
          />
        ) : (
          history.map((device) => {
            const isOnline = onlineIds.has(device.device_id);
            const isRevoked = device.status === "revoked";

            return (
              <div
                key={device.device_id}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-fill-white dark:bg-fill-dialog-surface border border-edge-gray-200 dark:border-edge-panel-border"
              >
                {/* Device icon + online dot */}
                <div className="relative flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-fill-indigo-50 dark:bg-fill-primary/15 flex items-center justify-center">
                    <Monitor className="w-4 h-4 text-ink-primary dark:text-ink-accent" />
                  </div>
                  {isOnline && !isRevoked && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-fill-green-500 border-2 border-edge-white dark:border-edge-dialog-surface" />
                  )}
                </div>

                {/* Name + last seen */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink-ink dark:text-ink-white truncate">
                    {device.device_name}
                  </p>
                  <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                    {isOnline && !isRevoked
                      ? "Online now"
                      : device.last_seen_at
                      ? `Last seen ${getTimeAgo(device.last_seen_at)}`
                      : "Never connected"}
                  </p>
                </div>

                {/* Status badge */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isRevoked ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-fill-red-100 text-ink-red-800 dark:bg-fill-red-900/40 dark:text-ink-red-300">
                      <ShieldOff className="w-3 h-3" />
                      Revoked
                    </span>
                  ) : isOnline ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-fill-emerald-100 text-ink-hex-065f46 dark:bg-fill-emerald-900/40 dark:text-ink-emerald-300">
                      <Wifi className="w-3 h-3" />
                      Online
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-fill-gray-100 text-ink-gray-500 dark:bg-fill-gray-700/60 dark:text-ink-gray-400">
                      <WifiOff className="w-3 h-3" />
                      Offline
                    </span>
                  )}

                  {/* Revoke button — only for active devices */}
                  {!isRevoked && (
                    <button
                      onClick={() => setRevokeTarget(device)}
                      className="text-[11px] font-medium text-ink-gray-400 hover:text-ink-red-500 dark:hover:text-ink-red-400 transition-colors duration-150 px-1.5 py-0.5 rounded"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Revoke confirmation dialog */}
      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke device"
        confirmLabel="Revoke"
        loadingLabel="Revoking…"
        isLoading={revoking}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
      >
        <p className="text-sm text-ink-gray-700 dark:text-ink-gray-300">
          Remove <span className="font-semibold">{revokeTarget?.device_name}</span> from
          your account? It will no longer be able to connect.
        </p>
      </ConfirmDialog>
    </>
  );
};

export default DeviceManagement;
